import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/* ================= LOADING SPINNER ================= */
const LoadingSpinner = ({ text = "Loading data…" }) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center py-5"
    style={{ minHeight: "300px" }}
  >
    <div
      className="spinner-border text-warning"
      role="status"
      style={{ width: "3rem", height: "3rem" }}
      aria-label="Loading"
    >
      <span className="visually-hidden">Loading...</span>
    </div>
    <p className="mt-3 text-muted fw-semibold">{text}</p>
  </div>
);

function RenewableAgenciesListing() {
  const [agencies, setAgencies] = useState([]);
  const [banners, setBanners] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const agenciesPerPage = 6;

  const slugify = (text = "") =>
    text
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [agenciesRes, bannersRes] = await Promise.all([
          axios.get(
            "https://backend-zkgn.onrender.com/api/renewable-agencies"
          ),
          axios.get(
            "https://backend-zkgn.onrender.com/api/banners/insidebanner"
          ),
        ]);

        setAgencies(agenciesRes.data || []);
        setBanners(bannersRes.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load renewable agencies");
        setAgencies([]);
      } finally {
        setLoading(false); // 🔥 KEY FIX
      }
    };

    fetchData();
  }, []);

  /* ================= FILTER ================= */
  const filteredAgencies = agencies.filter((agency) => {
    const name = agency.renew_agency_name?.toLowerCase() || "";
    const city = agency.renew_agency_city?.toLowerCase() || "";
    return (
      name.includes(search.toLowerCase()) ||
      city.includes(search.toLowerCase())
    );
  });

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredAgencies.length / agenciesPerPage);

  const indexOfLast = currentPage * agenciesPerPage;
  const indexOfFirst = indexOfLast - agenciesPerPage;
  const currentAgencies = filteredAgencies.slice(
    indexOfFirst,
    indexOfLast
  );

  const paginate = (page) => setCurrentPage(page);
  const goToPreviousPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  return (
    <div className="container-fluid my-5">
      <div className="row">
        <div className="col-lg-12">

          {/* ================= COUNT + SEARCH ================= */}
          <div className="row align-items-center mb-3">
            <div className="col-md-6 mb-2 mb-md-0">
              {!loading && !error && (
                <h5 className="fw-bold text-tertiry mb-0">
                  <strong>{filteredAgencies.length}</strong>{" "}
                  Renewable Agencies Found
                </h5>
              )}
            </div>

            <div className="col-md-6">
              <input
                type="text"
                className="form-control search-box w-100"
                placeholder="Search by name or city..."
                value={search}
                disabled={loading}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* ================= AGENCY LIST ================= */}
          {loading ? (
            <LoadingSpinner text="Fetching renewable agencies…" />
          ) : error ? (
            <div className="alert alert-danger text-center">
              {error}
            </div>
          ) : (
            <div className="row g-4">
              {currentAgencies.length > 0 ? (
                currentAgencies.map((agency) => (
                  <div className="col-md-6" key={agency.renew_id}>
                    <div className="card shadow-sm h-100 plant-card">
                      <div className="card-body">
                        <h5 className="card-title fw-bold">
                          <Link
                            to={`/renewableagency/${slugify(
                              agency.renew_agency_name
                            )}/${agency.renew_id}`}
                            className="text-decoration-none"
                          >
                            {agency.renew_agency_name}
                          </Link>
                        </h5>

                        <p className="mb-1">
                          <strong className="text-orange">Address:</strong>{" "}
                          {agency.renew_agency_address || "N/A"},{" "}
                          {agency.renew_agency_city || ""},{" "}
                          {agency.renew_agency_state || ""}
                        </p>

                        <p className="mb-0">
                          <strong className="text-orange">Phone:</strong>{" "}
                          {agency.renew_agency_phone || "N/A"}
                        </p>

                        {agency.renew_agency_email && (
                          <p className="mb-0">
                            <strong className="text-orange">Email:</strong>{" "}
                            {agency.renew_agency_email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="alert alert-warning text-center w-100">
                  No agencies found matching your search.
                </div>
              )}
            </div>
          )}

          {/* ================= PAGINATION ================= */}
          {totalPages > 1 && !loading && (
            <nav className="d-flex justify-content-center mt-4">
              <ul className="pagination pagination-sm flex-wrap shadow-sm">
                <li
                  className={`page-item ${
                    currentPage === 1 ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={goToPreviousPage}
                  >
                    ← Previous
                  </button>
                </li>

                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <li key={index} className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  ) : (
                    <li
                      key={index}
                      className={`page-item ${
                        currentPage === page ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => paginate(page)}
                      >
                        {page}
                      </button>
                    </li>
                  )
                )}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button className="page-link" onClick={goToNextPage}>
                    Next →
                  </button>
                </li>
              </ul>
            </nav>
          )}

        </div>
      </div>
    </div>
  );
}

export default RenewableAgenciesListing;
